import type { ReactNode } from "react";

import { DelegatedLink } from "@/components/shared/delegated-link";
import DesoAvatar from "@/components/shared/deso-avatar";
import { UserDisplayNameWithStatus } from "@/components/shared/user-display-name-with-status";
import getContentBlurredStyle from "@/utils/get-content-blurred-style";
import type { PartialAccount } from "@/utils/types";
import { getUserName } from "@/utils/usernames";

type SidebarTopResultsWidgetRowProps = Pick<
  PartialAccount,
  "username" | "publicKey" | "extraData"
> & {
  children: ReactNode;
  renderUsername?: ReactNode;
  usernamePrefix?: string;
  isCoin?: boolean;
  baseRedirectUrl?: string;
};

const MAX_USERNAME_LENGTH_BEFORE_GRADIENT = 4;

const SidebarTopResultsWidgetRow = ({
  username,
  publicKey,
  extraData,
  renderUsername,
  usernamePrefix = "",
  children,
  isCoin,
  baseRedirectUrl = "/",
}: SidebarTopResultsWidgetRowProps) => {
  return (
    <DelegatedLink
      href={`${baseRedirectUrl}${username ?? publicKey ?? ""}`}
      enabled={!!baseRedirectUrl}
      className="group block cursor-pointer"
    >
      <div
        className="
          flex w-full items-center justify-between border-b border-border-light
          p-2.5 transition-colors
          group-hover:bg-[color-mix(in_srgb,var(--card),transparent_12%)]
          last:border-none
        "
      >
        <div className="flex grow items-center gap-2">
          <DesoAvatar size={32} account={{ username, publicKey, extraData }} />
          <div className="flex grow flex-col">
            <div className="flex flex-col items-start gap-0.5">
              <div
                className="
                  flex w-full items-center gap-1 text-xs font-semibold
                  text-muted-foreground
                "
                style={getContentBlurredStyle(
                  username?.length,
                  MAX_USERNAME_LENGTH_BEFORE_GRADIENT,
                )}
              >
                <UserDisplayNameWithStatus
                  whaleSize={16}
                  baseRedirectUrl={baseRedirectUrl}
                  {...{
                    username: username ?? "",
                    publicKey: publicKey ?? "",
                    usernamePrefix,
                    extraData,
                    className:
                      "pl-0 w-[100px] font-sans text-xs text-ellipsis text-truncate underline-offset-0 whitespace-nowrap",
                  }}
                />
              </div>
              <span
                className="
                  inline-block w-[100px] overflow-hidden text-xs text-ellipsis
                  text-muted
                "
              >
                {renderUsername ||
                  getUserName({
                    account: {
                      username,
                      publicKey,
                    },
                    customPrefix: isCoin ? "$" : "@",
                  })}
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-0.5 text-right">{children}</div>
      </div>
    </DelegatedLink>
  );
};

export { SidebarTopResultsWidgetRow };
